import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import TradingChart from './components/TradingChart';
import OrderBook from './components/OrderBook';
import TradeForm from './components/TradeForm';
import CryptoSelector from './components/CryptoSelector';
import TradingHistory from './components/TradingHistory';
import PriceAlerts from './components/PriceAlerts';

const TradingInterface = () => {
  const cryptoList = [
  {
    id: 1,
    name: 'Bitcoin',
    symbol: 'BTC',
    price: 42950,
    change: 2.45,
    icon: "https://images.unsplash.com/photo-1631865382441-b00105810564",
    iconAlt: 'Golden Bitcoin cryptocurrency coin with B symbol on dark background'
  },
  {
    id: 2,
    name: 'Ethereum',
    symbol: 'ETH',
    price: 2280,
    change: 1.82,
    icon: "https://img.rocket.new/generatedImages/rocket_gen_img_117ea879d-1767158662331.png",
    iconAlt: 'Silver Ethereum cryptocurrency coin with diamond symbol on dark surface'
  },
  {
    id: 3,
    name: 'Binance Coin',
    symbol: 'BNB',
    price: 312,
    change: -0.65,
    icon: "https://img.rocket.new/generatedImages/rocket_gen_img_179301652-1764668859919.png",
    iconAlt: 'Yellow and black Binance cryptocurrency coin with BNB logo'
  },
  {
    id: 4,
    name: 'Solana',
    symbol: 'SOL',
    price: 98,
    change: 5.23,
    icon: "https://img.rocket.new/generatedImages/rocket_gen_img_1e2cc2138-1764643920948.png",
    iconAlt: 'Purple gradient Solana cryptocurrency coin with geometric symbol'
  },
  {
    id: 5,
    name: 'Cardano',
    symbol: 'ADA',
    price: 0.52,
    change: -1.34,
    icon: "https://img.rocket.new/generatedImages/rocket_gen_img_1f9cb1bb0-1764668859907.png",
    iconAlt: 'Blue Cardano cryptocurrency coin with circular symbol on dark background'
  }];


  const [selectedCrypto, setSelectedCrypto] = useState(cryptoList?.[0]);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [lastTrade, setLastTrade] = useState(null);

  const handleTradeExecute = (tradeData) => {
    setLastTrade(tradeData);
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 5000);
  };

  return (
    <>
      <Helmet>
        <title>Trading Interface - Kryptostash</title>
        <meta name="description" content="Trade cryptocurrencies with confidence on Kryptostash's secure and intuitive trading platform. Real-time charts, advanced order types, and educational resources for all experience levels." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-20 pb-8 px-4 md:px-6 lg:px-8">
          <div className="max-w-[1920px] mx-auto">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                  Trading Interface
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Trade cryptocurrencies with real-time market data and advanced tools
                </p>
              </div>

              <CryptoSelector
                cryptoList={cryptoList}
                selectedCrypto={selectedCrypto}
                onSelect={setSelectedCrypto} />

            </div>

            {showSuccessMessage && lastTrade &&
            <div className="mb-6 p-4 bg-success/10 border border-success rounded-lg flex items-start gap-3">
                <Icon name="CheckCircle" size={24} color="var(--color-success)" className="flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm md:text-base font-semibold text-success mb-1">
                    Trade Executed Successfully
                  </p>
                  <p className="text-xs md:text-sm text-foreground">
                    {lastTrade?.type === 'buy' ? 'Purchased' : 'Sold'} {lastTrade?.amount} {lastTrade?.crypto?.symbol} 
                    {' '}at ${lastTrade?.price?.toLocaleString()} for a total of ${lastTrade?.total?.toLocaleString()}
                  </p>
                </div>
                <button
                onClick={() => setShowSuccessMessage(false)}
                className="text-success hover:text-success/80 transition-colors">

                  <Icon name="X" size={20} />
                </button>
              </div>
            }

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
              <div className="lg:col-span-2">
                <TradingChart selectedCrypto={selectedCrypto} />
              </div>

              <div className="space-y-4 md:space-y-6">
                <TradeForm
                  selectedCrypto={selectedCrypto}
                  onTradeExecute={handleTradeExecute} />

              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
              <div className="lg:col-span-2">
                <OrderBook selectedCrypto={selectedCrypto} />
              </div>

              <div>
                <PriceAlerts cryptoList={cryptoList} />
              </div>
            </div>

            <TradingHistory />

            <div className="mt-8 bg-muted/30 rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={24} color="var(--color-primary)" className="flex-shrink-0" />
                <div>
                  <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">
                    Trading Tips for Beginners
                  </h3>
                  <ul className="space-y-2 text-xs md:text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Icon name="CheckCircle" size={16} className="flex-shrink-0 mt-0.5" color="var(--color-success)" />
                      <span>Start with small amounts to familiarize yourself with the platform and market dynamics</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="CheckCircle" size={16} className="flex-shrink-0 mt-0.5" color="var(--color-success)" />
                      <span>Use limit orders to control your entry and exit prices instead of market orders</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="CheckCircle" size={16} className="flex-shrink-0 mt-0.5" color="var(--color-success)" />
                      <span>Set price alerts to monitor market movements without constantly watching the charts</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="CheckCircle" size={16} className="flex-shrink-0 mt-0.5" color="var(--color-success)" />
                      <span>Never invest more than you can afford to lose and diversify your portfolio</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icon name="CheckCircle" size={16} className="flex-shrink-0 mt-0.5" color="var(--color-success)" />
                      <span>Review the order book to understand market depth and liquidity before placing large orders</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>);

};

export default TradingInterface;